import { Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import {
  CallcenterDeleteDialogComponent,
  CallcenterDeleteDialogData,
} from './callcenter-delete-dialog/callcenter-delete-dialog.component';
import {
  HLM_FORM_FIELD,
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmNativeSelectDirective,
  HlmSpinnerComponent,
  HlmSwitchComponent,
  HlmTooltipDirective,
  ToastService,
} from '../../../ui';
import { SessionService } from '../../../core/session/session.service';
import { extractErrorMessage } from '../../../shared/utils/extract-error-message';
import { LocalizationService } from '../../../core/services/localization.service';
import { Invoice, InvoiceService } from '../invoice.service';
import {
  BillingCreated,
  BillingInformation,
  CustomerBillingService,
  toRangeIso,
} from './customer-billing.service';
import {
  BILLING_GROUP_TO_CATEGORY_KEY,
  CALLCENTER_CATEGORY_KEY,
  isCallcenterCascadeFeature,
  FILTERABLE_BILLING_CATEGORIES,
  LegacyCategory,
} from './billing-categories';
import { FEATURE_LABELS_EN } from './feature-labels';
import {
  FeatureTexts,
  featureOneLineLabel,
  featureSearchText,
  featureSecondLine,
  resolveFeatureTexts,
} from './feature-texts.util';
import { FeatureHelpComponent } from './feature-label/feature-help.component';
import { AddBillingDialogComponent } from './add-billing-dialog/add-billing-dialog.component';

/**
 * Localization API coordinates for the billing feature/category bag — the
 * legacy `featureShowname/defTarif` namespace whose flat record contains BOTH
 * feature codes and group/category codes (EB-3886 / EB-3887).
 */
const FEATURE_SHOWNAME_GLOBAL_KEY = 'featureShowname';
const DEF_TARIF_CATEGORY = 'defTarif';
/** Localization API numeric language id for English. */
const ENGLISH_LANGUAGE_ID = '2';
/**
 * Localization API numeric language id for German — the **invoice text** of a
 * feature (what the customer reads on the invoice; tooltip, search and line-2
 * fallback, EB-4502). FIXED to German (EB-3887): brand/customer language is
 * deliberately NOT used.
 */
const SECONDARY_LANGUAGE_ID = '1';

@Component({
  selector: 'cmt-customer-billing',
  imports: [
    ReactiveFormsModule,
    NgClass,
    DatePipe,
    DecimalPipe,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmNativeSelectDirective,
    HlmSpinnerComponent,
    HlmSwitchComponent,
    HlmTooltipDirective,
    FeatureHelpComponent,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './customer-billing.component.html',
})
export class CustomerBillingComponent {
  private readonly session = inject(SessionService);
  private readonly billingService = inject(CustomerBillingService);
  private readonly localizationService = inject(LocalizationService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Customer's invoices (loaded by the parent invoice page). Used to map a
   * billing row's internal `invoiceId` (FK to invoice.id) to the official
   * `showReId` shown to the customer. The legacy `/contract/v2/rest/invoices`
   * endpoint that the parent uses appears to filter on print status, so
   * very recent invoices (e.g. April when we're still in early April) are
   * missing from this list. Missing entries are fetched on demand via
   * `getInvoiceById` and emitted to the parent through `(invoiceResolved)`
   * so they appear in the Invoices table too — no duplicate maps needed.
   */
  readonly invoices = input<Invoice[]>([]);

  /**
   * Emitted whenever a billing row references an invoice missing from the
   * `invoices` input and we successfully back-filled it via /invoice/v1.
   * Parent listens and appends the resolved record to its `invoices()`
   * signal — the next change-detection cycle then flows the entry back
   * down via `[invoices]` and the badge resolves automatically.
   */
  readonly invoiceResolved = output<Invoice>();

  /** Track which ids we've already attempted so we don't fire repeated GETs. */
  private readonly resolvedExtraIds = new Set<number>();

  /** Lookup map: parent-supplied invoices keyed by internal id → showReId. */
  private readonly invoiceShowReIdMap = computed(() => {
    const m = new Map<number, number>();
    for (const inv of this.invoices()) m.set(inv.id, inv.showReId);
    return m;
  });

  /** Returns the official invoice number (showReId), or `null` if unknown. */
  officialInvoiceNumber(internalId: number | null | undefined): number | null {
    if (internalId == null) return null;
    return this.invoiceShowReIdMap().get(internalId) ?? null;
  }

  /**
   * Emitted when the user clicks on an invoice badge in a billing row.
   * The parent (invoice.component) listens, expands the Invoices section,
   * filters it by this id and scrolls it into view.
   */
  readonly jumpToInvoice = output<number>();

  readonly categories: readonly LegacyCategory[] = FILTERABLE_BILLING_CATEGORIES;

  /**
   * Two-way collapse state. Exposed as a `model` (EB-3883) so the parent
   * invoice page's Collapse-all toggle and the default-collapsed initial state
   * can drive this section, while the local header chevron still toggles it.
   * Defaults to collapsed — Billing is a default-collapsed section.
   */
  readonly collapsed = model<boolean>(true);
  readonly loading = signal(false);
  readonly rows = signal<BillingInformation[]>([]);
  /** Free-text search — filters loaded rows across all visible fields. */
  readonly searchQuery = signal<string>('');
  /** Current filter category — tracked as signal so filteredRows can react. */
  readonly categoryKey = signal<string>('ALL');
  /** Rows currently being deleted on the server — hidden from the table until the call resolves. */
  private readonly deletingIds = signal<Set<number>>(new Set());
  /** Calls (`documentId|userAccountId`) whose cascade delete is in flight (EB-4384). */
  private readonly cascadingCalls = signal<ReadonlySet<string>>(new Set());

  /** BillingGroup id → backend `name` code, to pick the KV text of the row's group (EB-4502). */
  private readonly groupNames = signal<ReadonlyMap<number, string>>(new Map());

  private static readonly SHOW_FEATURE_CODES_KEY = 'cmt-billing-show-feature-codes';

  /**
   * Feature column shows the raw feature code instead of English + KV text
   * (EB-4502). Remembered per browser; storage may be unavailable, so every
   * access is guarded and the default (texts) still works.
   */
  readonly showFeatureCodes = signal(CustomerBillingComponent.readShowFeatureCodes());

  private static readShowFeatureCodes(): boolean {
    try {
      return localStorage.getItem(CustomerBillingComponent.SHOW_FEATURE_CODES_KEY) === '1';
    } catch {
      return false;
    }
  }

  setShowFeatureCodes(show: boolean): void {
    this.showFeatureCodes.set(show);
    try {
      localStorage.setItem(CustomerBillingComponent.SHOW_FEATURE_CODES_KEY, show ? '1' : '0');
    } catch {
      // Storage blocked (private mode, policy) — the toggle still works for this view.
    }
  }

  /**
   * Localization bags for feature/category labels (EB-3886 / EB-3887). The
   * `featureShowname/defTarif` bag is a flat record keyed by BOTH feature codes
   * AND group codes. Loaded once per customer (English id 2 + customer
   * language). Empty maps fall through to the static maps so the grid still
   * renders before/without translations.
   */
  private readonly labelsEn = signal<ReadonlyMap<string, string>>(new Map());
  private readonly labelsCustomerLang = signal<ReadonlyMap<string, string>>(new Map());

  private readonly defaultRange = this.buildDefaultRange();

  /**
   * Rows per page. Keeps each render fast and avoids dumping tens of
   * thousands of `<tr>` elements into the DOM at once.
   */
  private static readonly MAX_VISIBLE_ROWS = 100;

  readonly filterForm = new FormGroup(
    {
      categoryKey: new FormControl<string>('ALL', { nonNullable: true }),
      fromDate: new FormControl<string>(this.defaultRange.from, { nonNullable: true }),
      toDate: new FormControl<string>(this.defaultRange.to, { nonNullable: true }),
    },
    { validators: [CustomerBillingComponent.dateRangeValidator()] },
  );

  /** True when `fromDate > toDate` — drives Apply-button disable state. */
  readonly dateRangeInvalid = signal(false);

  /** Cross-field validator: fromDate must be ≤ toDate. Empty values are ignored. */
  private static dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const from = (group.get('fromDate')?.value as string | null) ?? '';
      const to = (group.get('toDate')?.value as string | null) ?? '';
      if (!from || !to) return null;
      return from > to ? { dateRangeInvalid: true } : null;
    };
  }

  /** UAC ids of the current customer — billing API is UAC-scoped. */
  readonly userAccountIds = computed(() =>
    this.session.userAccounts().map((ua) => ua.id),
  );

  readonly filteredRows = computed(() => {
    const key = this.categoryKey();
    const category = this.categories.find((c) => c.key === key) ?? this.categories[0];
    const query = this.searchQuery().trim().toLowerCase();
    const deleting = this.deletingIds();
    return this.rows().filter((r) => {
      if (deleting.has(r.id)) return false;
      if (!category.match(r.feature ?? '')) return false;
      if (query && !this.matchesQuery(r, query)) return false;
      return true;
    });
  });

  /**
   * Add Billing is disabled while the Callcenter Calls category is selected —
   * callcenter usage is generated automatically by the telephony pipeline and
   * must not be added manually.
   */
  readonly addBillingDisabled = computed(() => this.categoryKey() === CALLCENTER_CATEGORY_KEY);

  /**
   * All texts of a row's feature (EB-4502): English, KV text, invoice text —
   * consistent with the Add Billing dialog.
   *
   * The KV text depends on the group (`brief_scan` differs between Post Service
   * and Office Club), so the category comes from the row's `billingGroupId`,
   * falling back to the legacy `match` predicates.
   *
   * English text: `ENGLISH_OVERRIDES` (feature-texts.util.ts) wins over the bag;
   * English-bag-miss fallback chain:
   *   1. static English map FEATURE_LABELS_EN
   *   2. backend `featureShowName` (when it isn't just echoing the code)
   *   3. raw feature code as last resort
   */
  featureTexts(row: BillingInformation): FeatureTexts {
    const code = row.feature ?? '';
    const backend =
      row.featureShowName && row.featureShowName.trim().length > 0 && row.featureShowName !== code
        ? row.featureShowName
        : null;
    const fallback = FEATURE_LABELS_EN[code] ?? backend ?? code;
    return resolveFeatureTexts(code, this.categoryKeyOf(row), this.labelsEn(), this.labelsCustomerLang(), fallback);
  }

  /** Single-line `English (KV text)` label — delete confirm. */
  featureLabel(row: BillingInformation): string {
    return featureOneLineLabel(this.featureTexts(row));
  }

  /** Line 2 of the feature cell (KV text, else invoice text). */
  readonly featureSecondLine = featureSecondLine;

  private categoryKeyOf(row: BillingInformation): string | null {
    const groupName = row.billingGroupId == null ? undefined : this.groupNames().get(row.billingGroupId);
    const fromGroup = groupName ? BILLING_GROUP_TO_CATEGORY_KEY[groupName] : undefined;
    if (fromGroup) return fromGroup;
    const code = row.feature ?? '';
    return this.categories.find((c) => c.key !== 'ALL' && c.match(code))?.key ?? null;
  }

  /**
   * Category label of a row for the Category column (shown with the "All"
   * filter). Uses the same resolution as the KV text (`categoryKeyOf`: row
   * group first, legacy `match` predicates as fallback), so a code booked in
   * two groups (`brief_scan`) shows the group it was booked in (EB-4502).
   * Returns `null` when no category matches (template renders a dash).
   */
  categoryLabelOf(row: BillingInformation): string | null {
    const key = this.categoryKeyOf(row);
    return key ? (this.categories.find((c) => c.key === key)?.label ?? null) : null;
  }

  /** Zero-based page index currently shown. Reset to 0 when filters/search change. */
  readonly currentPage = signal(0);

  /** Page size — same as the old cap: keeps each render fast. */
  readonly pageSize = CustomerBillingComponent.MAX_VISIBLE_ROWS;

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize)),
  );

  /** Rows actually rendered for the current page. */
  readonly visibleRows = computed(() => {
    const all = this.filteredRows();
    const start = this.currentPage() * this.pageSize;
    return all.slice(start, start + this.pageSize);
  });

  /**
   * Document-cell rowspan per visible row id. Consecutive callcenter rows of the same
   * call (documentId + userAccountId) share one cell with one delete button, since that
   * button deletes them all anyway. First row of a run → run length, following rows → 0
   * (no cell). Secretarial rows of the same document are never merged — the cascade
   * does not delete them.
   *
   * @ticket EB-4384
   */
  private readonly documentCellSpans = computed(() => {
    const spans = new Map<number, number>();
    const rows = this.visibleRows();
    const runKey = (r: BillingInformation) => (this.isCascadeDeletable(r) ? this.callKey(r) : null);
    for (let i = 0; i < rows.length; ) {
      const key = runKey(rows[i]);
      let end = i + 1;
      while (key !== null && end < rows.length && runKey(rows[end]) === key) end++;
      spans.set(rows[i].id, end - i);
      for (let j = i + 1; j < end; j++) spans.set(rows[j].id, 0);
      i = end;
    }
    return spans;
  });

  /** Rowspan of the row's document cell: 0 = merged into the cell of a row above. */
  documentCellSpan(row: BillingInformation): number {
    return this.documentCellSpans().get(row.id) ?? 1;
  }

  readonly pageRange = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) return { from: 0, to: 0, total: 0 };
    const start = this.currentPage() * this.pageSize;
    return {
      from: start + 1,
      to: Math.min(start + this.pageSize, total),
      total,
    };
  });

  private matchesQuery(r: BillingInformation, q: string): boolean {
    const fields: (string | number | null | undefined)[] = [
      r.feature,
      r.featureShowName,
      // Every feature text (EB-4502): English, KV text (corrected + original),
      // invoice text — so staff find a row by any wording they know.
      featureSearchText(this.featureTexts(r)),
      r.info,
      r.userAccountId,
      r.documentId,
      r.invoiceId,
      r.amount,
      r.unitPrice,
    ];
    return fields.some((f) => f !== null && f !== undefined && String(f).toLowerCase().includes(q));
  }

  constructor() {
    // Keep the category signal in sync with the form control so filteredRows reacts.
    this.filterForm.controls.categoryKey.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        this.categoryKey.set(v ?? 'ALL');
      });

    // Mirror cross-field validity to a signal so the Apply button can react.
    this.filterForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dateRangeInvalid.set(this.filterForm.hasError('dateRangeInvalid'));
      });

    // M3: Always reset to page 0 when the filter inputs change so the user
    // never lingers on a stale page index after narrowing/broadening results.
    // Row-set-only changes (e.g. reload) still just clamp to avoid wiping a
    // valid page for the same filters.
    effect(() => {
      this.searchQuery();
      this.categoryKey();
      this.currentPage.set(0);
    });
    effect(() => {
      this.rows();
      const max = this.totalPages() - 1;
      if (this.currentPage() > max) this.currentPage.set(0);
    });

    // Resolve official invoice numbers for billing rows that reference
    // invoices not present in the parent's loaded list (e.g. very recent
    // invoices missing from the legacy /contract/v2/rest/invoices feed).
    // Fires on every rows / invoices change but only triggers a network
    // call once per missing id thanks to `resolvedExtraIds`.
    effect(() => {
      const known = new Set<number>();
      for (const inv of this.invoices()) known.add(inv.id);
      const missing: number[] = [];
      for (const row of this.rows()) {
        const id = row.invoiceId;
        if (id == null) continue;
        if (known.has(id)) continue;
        if (this.resolvedExtraIds.has(id)) continue;
        if (!missing.includes(id)) missing.push(id);
      }
      if (missing.length === 0) return;
      for (const id of missing) {
        this.resolvedExtraIds.add(id);
        this.invoiceService
          .getInvoiceById(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (inv) => this.invoiceResolved.emit(inv),
            // Failed lookups stay marked-as-resolved so we don't retry on
            // every row change. The badge falls back to "internal {id}".
            error: () => void 0,
          });
      }
    });

    // Reload whenever the customer (and therefore UAC list) changes.
    effect(() => {
      const uacs = this.userAccountIds();
      if (uacs.length > 0) this.reload();
    });

    // Pre-warm the Add Billing dialog's HTTP calls so the dialog opens instantly,
    // and load the label bags for the grid (English + German invoice text, EB-4502).
    // CustomerBillingService.prefetchAddDialogData caches the groups response;
    // the LocalizationService caches the translations so the dialog reuses them
    // when it opens.
    effect(() => {
      const customer = this.session.customer();
      if (!customer) return;
      this.billingService.prefetchAddDialogData(customer.id);
      // Guard against partial customer payloads (no brand) — labels fall back
      // to the static maps in that case.
      if (typeof customer.brandId === 'number') {
        this.loadLabelBags(customer.brandId);
      }

      // Group id → name lookup; shares the Add Billing dialog's cached `/billings/groups`.
      this.billingService
        .getGroups()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (groups) => this.groupNames.set(new Map(groups.map((g) => [g.id, g.name]))),
          error: () => void 0,
        });
    });
  }

  /**
   * Load the English + German (invoice text) i18n label bags for the billing grid
   * (EB-3886 / EB-3887 / EB-4502). Both come from the legacy `featureShowname/defTarif`
   * namespace whose flat record is keyed by BOTH feature codes AND group codes.
   *
   * `LocalizationService.getTranslations` caches per (brand, language,
   * globalKey, category) and swallows HTTP errors into an empty map, so a
   * failed request just keeps the static-map fallback. The Add Billing dialog
   * reuses these cached responses when it opens.
   */
  private loadLabelBags(brandId: number): void {
    this.localizationService
      .getTranslations(brandId, ENGLISH_LANGUAGE_ID, FEATURE_SHOWNAME_GLOBAL_KEY, DEF_TARIF_CATEGORY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((en) => this.labelsEn.set(en));
    // Invoice text, fixed to German (SECONDARY_LANGUAGE_ID) — brand/customer
    // language deliberately not used; brand stays the customer's brand.
    this.localizationService
      .getTranslations(brandId, SECONDARY_LANGUAGE_ID, FEATURE_SHOWNAME_GLOBAL_KEY, DEF_TARIF_CATEGORY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((local) => this.labelsCustomerLang.set(local));
  }

  /** Typed search-input handler — avoids `$any($event.target).value` in the template. */
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.searchQuery.set(value);
  }

  goToPage(page: number): void {
    const clamped = Math.max(0, Math.min(page, this.totalPages() - 1));
    this.currentPage.set(clamped);
  }

  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  toggle(): void {
    this.collapsed.update((v) => !v);
  }

  reload(): void {
    const uacs = this.userAccountIds();
    if (uacs.length === 0) {
      this.rows.set([]);
      return;
    }
    // Guard: the UI already disables Apply when the range is invalid, but
    // effects (UAC change) also trigger reload — short-circuit here too.
    if (this.filterForm.hasError('dateRangeInvalid')) {
      return;
    }
    const v = this.filterForm.getRawValue();
    this.loading.set(true);
    // Category filtering is client-side — we pass only date range to the backend
    // so the legacy "Anzeigen von" predicates apply identically against the full
    // BILLING window.
    this.billingService
      .searchForUserAccounts({
        userAccountIds: uacs,
        from: toRangeIso(v.fromDate, false),
        to: toRangeIso(v.toDate, true),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          // Sort by billingTime desc so newest appears first
          const sorted = [...rows].sort((a, b) =>
            (b.billingTime ?? '').localeCompare(a.billingTime ?? ''),
          );
          this.rows.set(sorted);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.rows.set([]);
          this.toast.open('Failed to load billing events', 'OK', { duration: 5000, tone: 'error' });
        },
      });
  }

  isInvoiced(row: BillingInformation): boolean {
    return row.invoiceId !== null;
  }

  /**
   * True when the user may delete this billing row — KV parity: the KV
   * "ausgewählte Zeile löschen" button is never disabled; `KundenBillingFrame`
   * only refuses invoiced rows. Callcenter features delete per call (cascade),
   * every other row on its own; a call whose cascade is in flight offers no button.
   * `entryEditable` is deliberately NOT used: the CMB-2840 DDL defines it as
   * "can be added manually", and the KV lets `_NE` / `cc_sekret*` / `sekret` /
   * `brief_kost` rows be deleted.
   *
   * @ticket EB-4384
   */
  isDeletable(row: BillingInformation): boolean {
    if (this.isInvoiced(row)) return false;
    if (isCallcenterCascadeFeature(row.feature)) {
      return this.isCascadeDeletable(row) && !this.cascadingCalls().has(this.callKey(row));
    }
    return true;
  }

  /**
   * True for a callcenter row that deletes together with all callcenter charges of
   * its call document (KV `deleteCCBillingByDokument`). Decided by the same feature
   * filter as the backend, not by BillingGroup: voicebot features (`callcenter_AI`,
   * `cc_min_AI`) have no group. Needs a real document id: rows with `documentId` 0
   * exist on test and the endpoint rejects ids < 1.
   *
   * @ticket EB-4384
   */
  isCascadeDeletable(row: BillingInformation): boolean {
    if (this.isInvoiced(row) || !isCallcenterCascadeFeature(row.feature)) return false;
    return row.documentId != null && row.documentId >= 1;
  }

  /**
   * Opens the Add Billing dialog, then reloads the list when the dialog returns
   * a successful creation result. Triggered by the "Add Billing" button in the
   * Billing Details card (EB-3642 Iteration 3 wiring).
   */
  openAddDialog(): void {
    const customer = this.session.customer();
    if (!customer) {
      this.toast.open('No customer loaded', 'OK', { duration: 3000, tone: 'default' });
      return;
    }
    const ref = this.dialog.open<
      AddBillingDialogComponent,
      { customerId: number; preselectedCategoryKey?: string },
      BillingCreated[] | null
    >(AddBillingDialogComponent, {
      data: {
        customerId: customer.id,
        // Carry the grid's category filter into the dialog so the user
        // doesn't have to re-select it (only when something specific is
        // active — 'ALL' means "no preference").
        preselectedCategoryKey: this.categoryKey(),
      },
      // Two-column mockup layout (What's being billed | Who & how much) needs
      // ~880px to render without clipping the footer Add button.
      width: '100%',
      maxWidth: '880px',
      autoFocus: false,
      restoreFocus: true,
    });
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.length > 0) {
        this.reload();
        // TODO(EB-3642): route via LocalizationService once invoice.toast.* keys are seeded
        this.toast.open('Billing entry saved', 'OK', { duration: 3000 });
      }
    });
  }

  /**
   * Confirm-then-delete flow: open a Delete Billing dialog; only on confirm
   * hide the row (optimistic) and fire DELETE /billings/{id}. On failure the
   * row is restored and an error toast is shown.
   */
  deleteRow(row: BillingInformation): void {
    if (row.invoiceId !== null) return; // guarded by UI disabled state anyway
    if (this.isCascadeDeletable(row)) {
      this.deleteCallcenterCharges(row);
      return;
    }

    const label = this.featureLabel(row) || `#${row.id}`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Billing Row',
        message: `Delete "${label}"? This action cannot be undone.`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'delete',
      },
      panelClass: 'cmt-hlm-dialog',
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => {
      if (confirmed) this.finalizeDelete(row.id);
    });
  }

  private finalizeDelete(id: number): void {
    // Optimistic: hide immediately so the user gets instant feedback.
    this.deletingIds.update((s) => new Set(s).add(id));

    this.billingService.delete(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.rows.update((rows) => rows.filter((r) => r.id !== id));
        this.deletingIds.update((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
        this.toast.open('Billing row deleted', 'OK', { duration: 3000 });
      },
      error: () => {
        // Restore the row — backend refused.
        this.deletingIds.update((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
        this.toast.open('Failed to delete billing row', 'OK', { duration: 5000, tone: 'error' });
      },
    });
  }

  /**
   * Splits the call's loaded rows (all loaded rows — not just the current filter,
   * search or page) into what the cascade deletes, what stays, and what is already
   * invoiced (the backend then refuses the whole delete). Same feature filter and
   * user-account scope as the backend.
   *
   * @ticket EB-4384
   */
  callcenterDeleteScope(row: BillingInformation): CallcenterDeleteDialogData {
    const call = this.rows().filter(
      (r) => r.documentId === row.documentId && r.userAccountId === row.userAccountId,
    );
    const cascade = call.filter((r) => isCallcenterCascadeFeature(r.feature));
    const label = (r: BillingInformation) => this.featureTexts(r).english;
    return {
      documentId: row.documentId as number,
      deleted: cascade.filter((r) => !this.isInvoiced(r)).map(label),
      // Only rows the operator can still delete on their own (invoiced rows are read-only).
      kept: call.filter((r) => !isCallcenterCascadeFeature(r.feature) && !this.isInvoiced(r)).map(label),
      invoiced: cascade.filter((r) => this.isInvoiced(r)).map(label),
    };
  }

  /** Identifies a call for the cascade: the backend deletes per document + user account. */
  private callKey(row: BillingInformation): string {
    return `${row.documentId}|${row.userAccountId}`;
  }

  /**
   * Cascade flow for callcenter rows: one confirm, one backend call that deletes every
   * callcenter charge of the row's call document for its user account. Siblings may sit
   * outside the current filter/page (and the backend filter also hits rows outside the
   * callcenter group), so the list is reloaded; the call's loaded cascade rows are dropped
   * locally first, because `reload()` skips while the date fields hold an invalid range.
   * A failure other than 409 also reloads: the DELETE may have committed before the
   * response was lost (gateway timeout). While the request runs the call offers no
   * delete button (no duplicate DELETE).
   *
   * @ticket EB-4384
   */
  private deleteCallcenterCharges(row: BillingInformation): void {
    const documentId = row.documentId as number; // isCascadeDeletable guarantees >= 1
    const dialogRef = this.dialog.open<CallcenterDeleteDialogComponent, CallcenterDeleteDialogData, boolean>(
      CallcenterDeleteDialogComponent,
      { data: this.callcenterDeleteScope(row), panelClass: 'cmt-hlm-dialog' },
    );

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed) => {
      if (!confirmed) return;
      const key = this.callKey(row);
      this.cascadingCalls.update((s) => new Set(s).add(key));
      this.billingService
        .deleteCallcenterCharges(documentId, row.userAccountId)
        .pipe(
          finalize(() =>
            this.cascadingCalls.update((s) => {
              const next = new Set(s);
              next.delete(key);
              return next;
            }),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: ({ deletedCount }) => {
            this.rows.update((rows) =>
              rows.filter(
                (r) => !(this.callKey(r) === key && isCallcenterCascadeFeature(r.feature) && !this.isInvoiced(r)),
              ),
            );
            this.reload();
            if (deletedCount > 0) {
              const noun = deletedCount === 1 ? 'charge' : 'charges';
              this.toast.open(`${deletedCount} callcenter ${noun} deleted`, 'OK', { duration: 3000 });
            } else {
              this.toast.open('No callcenter charges deleted', 'OK', { duration: 3000, tone: 'default' });
            }
          },
          error: (err) => {
            // 409 = backend refused, nothing deleted; anything else may have committed.
            if (err?.status !== 409) this.reload();
            this.toast.open(extractErrorMessage(err, 'Failed to delete callcenter charges'), 'OK', {
              duration: 8000,
              tone: 'error',
            });
          },
        });
    });
  }

  /**
   * Default range: last 30 days. Matches the legacy KundenBilling "Zeitraum"
   * default so users moving between CMT 1.x and 2.0 see the same initial
   * window and don't have to re-enter dates on every load.
   */
  private buildDefaultRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    return { from: fmt(from), to: fmt(to) };
  }
}
